<template>
  <el-container class="layout-container">
    <!-- 左侧整列：品牌区 + 菜单 -->
    <el-aside width="240px" class="layout-sidebar-column">
      <div class="sidebar-brand">
        <div class="brand-icon">
          <i class="el-icon-s-marketing"></i>
        </div>
        <h2 class="brand-title">AI骨干人才保留助手</h2>
      </div>

      <div class="sidebar-menu-wrapper">
        <!-- 任务层头部：返回按钮 -->
        <div v-if="isTaskLayer" class="sidebar-header task-sidebar-header">
          <div v-if="$hasPermission('task:manage')" class="task-header-back" @click="goToTaskList">
            <i class="el-icon-back"></i>
            <span>返回任务列表</span>
          </div>
        </div>

        <el-menu :default-active="activeMenuIndex" router class="sidebar-menu">
          <!-- 首页：仅主管显示 -->
          <el-menu-item v-if="isManager" index="/">
            <i class="el-icon-s-home"></i>
            <span slot="title">首页</span>
          </el-menu-item>

          <!-- 主管：前台功能菜单 -->
          <template v-if="isManager">
            <el-menu-item index="/select">
              <i class="el-icon-user"></i>
              <span slot="title">评估员工离职风险</span>
            </el-menu-item>
            <el-menu-item index="/dashboard">
              <i class="el-icon-s-grid"></i>
              <span slot="title">团队风险热力图</span>
            </el-menu-item>
            <el-menu-item index="/correction">
              <i class="el-icon-edit"></i>
              <span slot="title">离职特征库修正</span>
            </el-menu-item>
            <el-menu-item index="/ab-compare">
              <i class="el-icon-s-data"></i>
              <span slot="title">ABC风险对比分析</span>
            </el-menu-item>
            <el-menu-item index="/survey-create">
              <i class="el-icon-link"></i>
              <span slot="title">生成员工调研链接</span>
            </el-menu-item>
          </template>

          <!-- 管理员：后台管理菜单（根据当前层动态显示） -->
          <template v-if="isAdminRole">
            <!-- 平台层：扁平一级菜单 -->
            <template v-if="isPlatformLayer">
              <template v-for="item in visiblePlatformMenus">
                <el-menu-item :key="item.route" :index="item.index" @click.native="navigateTo(item.route)">
                  <i :class="item.icon"></i>
                  <span slot="title">{{ item.title }}</span>
                </el-menu-item>
              </template>
            </template>
            <!-- 任务层：扁平一级菜单 -->
            <template v-else>
              <template v-for="item in visibleTaskMenuItems">
                <el-menu-item :key="item.route" :index="item.index" @click.native="navigateTo(item.route)">
                  <i :class="item.icon"></i>
                  <span slot="title">{{ item.title }}</span>
                </el-menu-item>
              </template>
            </template>
          </template>
        </el-menu>
      </div>
    </el-aside>

    <!-- 右侧区域：顶部栏 + 内容区 -->
    <el-container class="layout-right">
      <!-- 顶部导航 -->
      <el-header class="layout-header">
        <div class="header-left">
          <div v-if="isTaskLayer" class="header-task-name">
            <i class="el-icon-s-cooperation"></i>
            <span :title="currentTaskName">{{ currentTaskName }}</span>
          </div>
        </div>
        <div class="header-actions">
          <template v-if="isManager">
            <el-button type="text" class="header-btn" @click="showTutorial = true">
              <i class="el-icon-question"></i> 引导教程
            </el-button>
            <el-divider direction="vertical"></el-divider>
          </template>

          <div class="user-info">
            <span class="user-email">{{ userEmail }}</span>
            <span class="user-role">{{ userRoleText }}</span>
          </div>
          <el-button size="small" class="btn-secondary" @click="openChangePwd">
            <i class="el-icon-lock"></i> 修改密码
          </el-button>
          <el-button size="small" class="btn-secondary" @click="logout">
            <i class="el-icon-switch-button"></i> 退出
          </el-button>
        </div>
      </el-header>

      <!-- 主内容区 -->
      <el-main :class="['layout-main', { 'admin-layout': isAdminPage }]">
        <transition name="page" mode="out-in">
          <router-view />
        </transition>
      </el-main>
    </el-container>

    <!-- 修改密码弹窗 -->
    <el-dialog title="修改密码" :visible.sync="showChangePwd" width="400px" :append-to-body="true">
      <el-form :model="pwdForm" label-width="80px">
        <el-form-item label="当前密码">
          <el-input v-model="pwdForm.current" type="password" show-password placeholder="请输入当前密码" />
        </el-form-item>
        <el-form-item label="新密码">
          <el-input v-model="pwdForm.newPwd" type="password" show-password placeholder="新密码（至少6位）" />
        </el-form-item>
        <el-form-item label="确认密码">
          <el-input v-model="pwdForm.confirm" type="password" show-password placeholder="再次输入新密码" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showChangePwd = false">取消</el-button>
        <el-button type="primary" @click="confirmChangePwd">确定</el-button>
      </span>
    </el-dialog>

    <!-- 引导教程 -->
    <el-dialog title="引导教程" :visible.sync="showTutorial" width="600px" class="tutorial-dialog">
      <div class="tutorial-content">
        <h4><i class="el-icon-star-on" style="color: var(--color-primary);"></i> 欢迎使用 AI 骨干人才保留助手</h4>
        <p>本工具帮助管理者量化评估团队成员的离职风险，并提供具体的保留建议。</p>
        <h4><i class="el-icon-s-opportunity" style="color: var(--color-primary);"></i> 核心功能</h4>
        <ol>
          <li><b>评估员工离职风险</b>：选择员工，勾选观察到的行为特征，系统实时计算风险分数</li>
          <li><b>团队风险热力图</b>：查看团队所有成员的风险分布，优先关注高风险员工</li>
          <li><b>离职特征库修正</b>：针对已离职员工回溯行为，帮助系统优化预测模型</li>
          <li><b>ABC风险对比</b>：对比三名员工的行为差异，制定针对性策略</li>
          <li><b>员工调研</b>：向员工发送匿名调研，收集真实的离职原因</li>
        </ol>
        <h4><i class="el-icon-warning" style="color: var(--color-medium);"></i> 评分规则</h4>
        <ul>
          <li><span class="risk-dot" style="background: var(--color-low);"></span> {{ tutorialRules.low }}</li>
          <li><span class="risk-dot" style="background: var(--color-medium);"></span> {{ tutorialRules.medium }}</li>
          <li><span class="risk-dot" style="background: var(--color-high);"></span> {{ tutorialRules.high }}</li>
        </ul>
      </div>
    </el-dialog>
  </el-container>
</template>

<script>
import { rawRemove } from '../utils/storage'
import { hasMenuPermission } from '../utils/permission'

export default {
  name: 'Layout',
  data() {
    return {
      showTutorial: false,
      showChangePwd: false,
      pwdForm: { current: '', newPwd: '', confirm: '' },
      platformMenuItems: [
        { title: '管理员列表', route: '/admin/admins', index: '/admin/admins', icon: 'el-icon-s-custom', permission: 'admin:manage' },
        { title: '任务列表', route: '/admin/tasks', index: '/admin/tasks', icon: 'el-icon-s-cooperation', permission: 'task:manage' }
      ]
    }
  },
  computed: {
    role() {
      return (this.$store.state.user && this.$store.state.user.role) || 'manager'
    },
    isManager() {
      return this.role === 'manager'
    },
    isAdminRole() {
      return this.role === 'super_admin' || this.role === 'admin'
    },
    userRoleText() {
      const map = { manager: '主管', super_admin: '超级管理员', admin: '管理员' }
      return map[this.role] || '主管'
    },
    userEmail() {
      const user = this.$store.state.user
      return user ? user.email : ''
    },
    isAdminPage() {
      return this.$route.path.startsWith('/admin')
    },
    isTaskLayer() {
      return this.$route.path.startsWith('/admin/tasks/')
    },
    isPlatformLayer() {
      return this.isAdminPage && !this.isTaskLayer
    },
    currentTaskId() {
      return this.$store.getters.currentTaskId
    },
    currentTaskName() {
      const task = this.$store.state.tasks.currentTask
      return (task && task.task_name) || '未选择任务'
    },
    myTasks() {
      return this.$store.getters.myTasks || []
    },
    visiblePlatformMenus() {
      if (this.role === 'super_admin') return this.platformMenuItems
      // 普通管理员只能看到任务列表
      if (this.role === 'admin') {
        return this.platformMenuItems.filter(item => item.permission === 'task:manage')
      }
      return this.platformMenuItems.filter(item => {
        return hasMenuPermission(this.$store, item.permission)
      })
    },
    visibleTaskMenuItems() {
      if (this.role === 'super_admin') return this.taskMenuItems
      return this.taskMenuItems.filter(item => hasMenuPermission(this.$store, item.permission))
    },
    taskMenuItems() {
      const taskId = this.currentTaskId
      if (!taskId) return []
      const prefix = `/admin/tasks/${taskId}`
      return [
        { title: '评估对象管理', route: `${prefix}/employees`, index: `${prefix}/employees`, permission: 'employee:view', icon: 'el-icon-user' },
        { title: '行为库管理', route: `${prefix}/behaviors`, index: `${prefix}/behaviors`, permission: 'behavior:view', icon: 'el-icon-s-check' },
        { title: '评估记录管理', route: `${prefix}/evaluations`, index: `${prefix}/evaluations`, permission: 'evaluation:view', icon: 'el-icon-document' },
        { title: '评估规则配置', route: `${prefix}/config?tab=scoring`, index: `${prefix}/config-scoring`, permission: 'scoring:view', icon: 'el-icon-s-tools' },
        { title: '评估报告配置', route: `${prefix}/contents`, index: `${prefix}/contents`, permission: 'content:view', icon: 'el-icon-document-copy' },
        { title: 'AI 报告配置', route: `${prefix}/ai-report`, index: `${prefix}/ai-report`, icon: 'el-icon-cpu' },
        { title: '调研题目配置', route: `${prefix}/departure-survey`, index: `${prefix}/departure-survey`, permission: 'departure_feedback:view', icon: 'el-icon-message' }
      ]
    },
    tutorialRules() {
      const r = this.$store.state.settings.settings.rules || {}
      const t = this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
      return {
        low: r.lowDesc || '低风险：0-' + t.lowMax + '分（绿色）',
        medium: r.mediumDesc || '中风险：' + (t.lowMax + 1) + '-' + t.mediumMax + '分（橙色）',
        high: r.highDesc || '高风险：≥' + (t.mediumMax + 1) + '分（红色）'
      }
    },
    activeMenuIndex() {
      const path = this.$route.path
      const tab = this.$route.query.tab
      // 如果是 config 页面且有 tab 参数
      if (path.includes('/config') && tab) {
        // 任务层或平台层的 config 路由，统一处理
        const base = path.split('?')[0]
        return base + '-' + tab
      }
      return path
    },
  },
  methods: {
    logout() {
      rawRemove('role')
      this.$store.commit('SET_USER', null)
      this.$store.commit('CLEAR_CURRENT_TASK')
      this.$router.push('/login')
    },
    navigateTo(route) {
      if (this.$route.fullPath !== route) {
        this.$router.push(route)
      }
    },
    goToTaskList() {
      this.$store.commit('CLEAR_CURRENT_TASK')
      this.$router.push('/admin/tasks')
    },
    openChangePwd() {
      this.pwdForm = { current: '', newPwd: '', confirm: '' }
      this.showChangePwd = true
    },
    confirmChangePwd() {
      const { current, newPwd, confirm } = this.pwdForm
      if (!current || !newPwd || !confirm) {
        this.$message.error('请填写完整')
        return
      }
      if (newPwd.length < 6) {
        this.$message.error('新密码最少6位')
        return
      }
      if (newPwd !== confirm) {
        this.$message.error('两次输入不一致')
        return
      }
      const user = this.$store.state.user
      const email = user ? user.email : ''
      // 校验当前密码
      const admins = this.$store.state.admins.admins || []
      const admin = admins.find(a => a.email === email)
      const currentPwd = admin ? admin.password : ''
      if (current !== currentPwd) {
        this.$message.error('当前密码错误')
        return
      }
      this.$store.commit('UPDATE_ADMIN', { ...admin, password: newPwd })
      this.$message.success('密码已修改')
      this.showChangePwd = false
    }
  }
}
</script>

<style scoped>
.layout-container {
  height: 100vh;
  background: var(--color-bg);
}

/* 左侧整列 */
.layout-sidebar-column {
  background: var(--color-surface);
  border-right: 1px solid var(--color-border);
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
}

/* 品牌区 */
.sidebar-brand {
  height: 64px;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: 0 var(--space-lg);
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.sidebar-brand .brand-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #F2C94C 0%, #D4A72E 100%);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-accent);
  font-size: 18px;
  flex-shrink: 0;
}

.sidebar-brand .brand-title {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text);
  white-space: nowrap;
  flex-shrink: 0;
}

/* 菜单包裹区：占满剩余高度，可滚动 */
.sidebar-menu-wrapper {
  flex: 1;
  overflow-y: auto;
  padding: var(--space-lg) 0;
}

/* 右侧区域 */
.layout-right {
  flex: 1;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
}

/* 顶部栏 */
.layout-header {
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px !important;
  padding: 0 var(--space-xl);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: var(--space-xl);
  flex: 1;
  min-width: 0;
}

.header-task-name {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
  background: rgba(242, 201, 76, 0.1);
  padding: 6px 14px;
  border-radius: var(--radius-md);
  max-width: 320px;
}

.header-task-name i {
  color: var(--color-accent);
  font-size: 16px;
  flex-shrink: 0;
}

.header-task-name span {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: var(--space-md);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 6px;
}
.user-email {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.header-btn {
  color: var(--color-text-secondary) !important;
  font-weight: 500;
}

.header-btn:hover {
  color: var(--color-primary) !important;
}

.user-role {
  color: var(--color-accent);
  font-size: 14px;
  font-weight: 600;
  background: rgba(242, 201, 76, 0.15);
  padding: 4px 12px;
  border-radius: var(--radius-full);
}

/* 侧边栏头部（平台/任务/功能导航） */
.sidebar-header {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: 0 var(--space-xl);
  margin-bottom: var(--space-lg);
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.task-sidebar-header {
  align-items: flex-start;
  padding: 0 var(--space-lg);
  margin-bottom: var(--space-lg);
}

.task-header-back {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--color-text-muted);
  cursor: pointer;
  padding: 4px 0;
  transition: color 0.2s;
}

.task-header-back:hover {
  color: var(--color-primary);
}

.sidebar-menu {
  border: none;
  background: transparent;
}

.layout-main {
  background: var(--color-bg);
  padding: var(--space-2xl);
  overflow-y: auto;
}

.layout-main.admin-layout {
  padding: var(--space-lg);
}

/* 引导教程 */
.tutorial-content h4 {
  color: var(--color-text);
  font-size: 16px;
  font-weight: 600;
  margin: var(--space-xl) 0 var(--space-md) 0;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

.tutorial-content h4:first-child {
  margin-top: 0;
}

.tutorial-content p {
  color: var(--color-text-secondary);
  line-height: 1.6;
}

.tutorial-content ol, .tutorial-content ul {
  color: var(--color-text-secondary);
  line-height: 1.8;
  padding-left: var(--space-xl);
}

.tutorial-content li {
  margin-bottom: var(--space-sm);
}

.risk-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: var(--radius-full);
  margin-right: var(--space-sm);
}

/* 页面过渡动画 */
.page-enter-active, .page-leave-active {
  transition: opacity var(--duration-slow) var(--ease-out),
              transform var(--duration-slow) var(--ease-out);
}
.page-enter {
  opacity: 0;
  transform: translateY(16px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
